<template>
  <div class="docs-wrapper q-pa-md">
    <section style="max-width:600px;margin: auto;">
      <q-breadcrumbs active-color="blue-10" separator-color="blue-10" class="text-blue-grey-10">
        <q-breadcrumbs-el class="cursor-pointer" label="Главная" icon="home" @click="goToMain" />
        <q-breadcrumbs-el label="Документы" icon="library_books" />
      </q-breadcrumbs>
      <div class="text-h5 text-weight-bold q-mb-xs">
        Медицинские документы
      </div>
    <!--
      <div v-if="patientName" class="text-subtitle1 text-weight-medium q-mb-lg">
        {{ patientName }}
      </div> -->

      <!-- <q-banner
        class="info-banner q-mb-lg"
        rounded
      >
        <template #avatar>
          <div class="info-accent-bar" />
        </template>
        В личный кабинет приходят только документы, выданные после 1 января 2024 г.
        <br />
        <a href="#" class="text-primary">Как посмотреть документы</a>
      </q-banner> -->

      <div class="segment-toggle q-mb-md">
        <button
          :class="['segment-btn', !viewByVisits ? 'segment-btn--active' : '']"
          @click="viewByVisits = false"
        >
          <q-icon name="filter_list" size="16px" class="q-mr-xs" />
          По категориям
        </button>
        <button
          :class="['segment-btn', viewByVisits ? 'segment-btn--active' : '']"
          @click="viewByVisits = true"
        >
          <q-icon name="date_range" size="16px" class="q-mr-xs" />
          По визитам
        </button>
      </div>

      <div v-if="!viewByVisits" class="q-mb-lg">

        <div class="filter-label q-mb-xs">Выберите категорию</div>
        <q-select
          v-model="selectedCategory"
          :options="categoryOptions"
          outlined
          behavior="menu"
          dense
          emit-value
          map-options
          class="q-mb-md filter-select"
        />

        <div class="filter-label q-mb-xs">Период</div>
        <q-input
          v-model="periodText"
          outlined
          dense
          mask="##.##.#### — ##.##.####"
          fill-mask
          :error="!!errorMessage"
          :error-message="errorMessage"
          @blur="parseInput"
          class="filter-input"
        >
          <template #append>
            <q-icon name="event" color="primary" class="cursor-pointer">
              <q-popup-proxy ref="datePopup">
                <q-date
                  v-model="dateRange"
                  range
                  mask="DD.MM.YYYY"
                  color="blue-10"
                  text-color="white"
                  subtitle="Задайте период"
                  :options="dateRangeOptions"
                  @range-start="rememberRangeStart"
                  @range-end="closePopup"
                  @update:model-value="handleRangeUpdate"
                >
                  <div class="row items-center justify-end q-gutter-sm">
                    <q-btn
                      :label="`До сегодня (${formatDate(TODAY)})`"
                      @click.stop="setPeriod"
                      color="primary"
                      flat
                    />
                  </div>
                </q-date>
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
      </div>

      <div class="sort-links-row q-mb-md">
        <button
          class="sort-link"
          @click="toggleDateSort"
        >
          <q-icon name="swap_vert" size="18px" class="q-mr-xs" />
          {{ dateSortAsc ? 'Сначала старые' : 'Сначала новые' }}
        </button>
        <button
          class="sort-link"
          @click="toggleTimeSort"
        >
          <q-icon name="swap_vert" size="18px" class="q-mr-xs" />
          {{ timeSortAsc ? 'Время: сначала ранние' : 'Время: сначала поздние' }}
        </button>
      </div>

      <div v-if="loading" class="flex flex-center q-pa-xl">
        <q-spinner size="40px" color="primary" />
      </div>

      <div
        v-if="!loading && documents.length === 0"
        class="text-grey text-body2"
      >
        Документы отсутствуют
      </div>

      <div v-if="!viewByVisits && !loading">
        <div
          v-for="doc in filteredByCategoryAndPeriod"
          :key="doc.id_document"
          :class="['doc-card q-mb-sm', { 'doc-card--wide-actions': showRefreshBtn(doc) }]"
          @click="openInNewTab(doc)"
        >
          <div class="doc-date-label text-caption text-grey q-mb-xs">
            {{ doc.date_doc }}
            <span
              v-if="formatTimeOnly(doc)" class="doc-time-label"> · {{ formatTimeOnly(doc) }}</span>
            <template v-if="getSignStatus(doc)">
              ·
              <span :class="['doc-sign-status', getSignStatusClass(doc)]">
                {{ getSignStatus(doc)?.signed_str }}
              </span>
            </template>
          </div>
          <div class="doc-title">
            {{ doc.doc_name }}
          </div>

          <div class="doc-actions" @click.stop>
            <q-btn
              v-if="showRefreshBtn(doc)"
              flat
              round
              dense
              icon="refresh"
              color="primary"
              :loading="isDocBusy(doc)"
              @click="refreshSignStatuses"
            />
            <q-btn flat round dense icon="more_vert">
              <q-menu>
                <q-list style="min-width: 140px">
                  <q-item clickable v-close-popup @click="download(doc)">
                    <q-item-section>Скачать</q-item-section>
                  </q-item>
                  <q-item
                    v-if="canSign(doc)"
                    clickable
                    v-close-popup
                    :disable="isDocBusy(doc)"
                    @click="handleSign(doc)"
                  >
                    <q-item-section>{{ getSignMenuLabel(doc) }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </div>

      <div v-else-if="!loading">
        <div
          v-if="groupedByVisit.length === 0"
          class="text-grey"
        >
          Документы по визитам отсутствуют
        </div>

        <div
          v-for="group in groupedByVisit"
          :key="group.date"
          class="visit-group q-mb-lg"
        >
          <div class="visit-header q-mb-sm">
            {{ group.label }}
          </div>

          <div
            v-for="doc in group.docs"
            :key="doc.id_document"
            :class="['doc-card q-mb-sm', { 'doc-card--wide-actions': showRefreshBtn(doc) }]"
            @click="openInNewTab(doc)"
          >
            <div class="doc-date-label text-caption text-grey q-mb-xs">
              {{ doc.date_doc }}
              <span
                v-if="formatTimeOnly(doc)"
                class="doc-time-label"> · {{ formatTimeOnly(doc) }}</span>
              <template v-if="getSignStatus(doc)">
                ·
                <span :class="['doc-sign-status', getSignStatusClass(doc)]">
                  {{ getSignStatus(doc)?.signed_str }}
                </span>
              </template>
            </div>
            <div class="doc-title">
              {{ doc.doc_name }}
            </div>
            <div class="doc-actions" @click.stop>
              <q-btn
                v-if="showRefreshBtn(doc)"
                flat
                round
                dense
                icon="refresh"
                color="primary"
                :loading="isDocBusy(doc)"
                @click="refreshSignStatuses"
              />
              <q-btn flat round dense icon="more_vert">
                <q-menu>
                  <q-list style="min-width: 140px">
                    <q-item clickable v-close-popup @click="download(doc)">
                      <q-item-section class="text-accent">Скачать</q-item-section>
                    </q-item>
                    <q-item
                      v-if="canSign(doc)"
                      clickable
                      v-close-popup
                      :disable="isDocBusy(doc)"
                      @click="handleSign(doc)"
                    >
                      <q-item-section class="text-accent">
                        {{ getSignMenuLabel(doc) }}
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!loading && filteredByCategoryAndPeriod.length === 0 && !viewByVisits"
        class="text-grey text-body2"
      >
        Документы по категории за выбранный период отсутствуют
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { useQuasar } from 'quasar';
import {
  ref,
  computed,
  onMounted,
  watch,
  PropType,
} from 'vue';
// eslint-disable-next-line import/no-cycle
import { useEcoHospitalStore } from '~store/ecohospital/ecohospitalModule';
import { GoskluchSignStatus } from '~store/ecohospital/types';
import { useGoskluchSign } from '~utils/useGoskluchSign';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '~store/index';

export default { name: 'PatientDocuments' };
</script>

<script setup lang="ts">
export type UserDocument = {
  att_type: number;
  checksum: number;
  date_doc: string;
  datebegin: string;
  doc_name: string;
  doc_type: number;
  file_ext: string;
  id_document: number;
  id_patient: number;
  is_napravlenie: number;
  goskluch: boolean;
  tg: string;
  time_doc: string;
};

export type GroupedDocuments = {
  header: string;
  documents: UserDocument[];
};

const props = defineProps({
  category: String as PropType<string>,
});

const $q = useQuasar();

const ecoStore = useEcoHospitalStore();

const documents = ref<UserDocument[]>([]);
const loading = ref(false);

const MIN_DATE = new Date(2019, 0, 1);
const TODAY = new Date();

const RESEARCH_DOC_TYPES = new Set([
  85, 84, 28, 27, 116, 172, 101, 102, 122, 124, 123, 100, 36, 98, 103, 187, 99,
]);
const CONCLUSION_DOC_TYPES = new Set([
  44, 30, 192, 153, 202,
]);

const viewByVisits = ref(false);
const selectedCategory = ref('directions');

// true = от старых к новым / от раннего к позднему, false = наоборот
const dateSortAsc = ref(false);
const timeSortAsc = ref(false);

const toggleDateSort = () => {
  dateSortAsc.value = !dateSortAsc.value;
};

const toggleTimeSort = () => {
  timeSortAsc.value = !timeSortAsc.value;
};

const datePopup = ref();

const goToMain = () => {
  router.push({
    name: 'HealthPageFull',
  });
};

onMounted(async () => {
  try {
    loading.value = true;
    if (ecoStore.documents.length === 0) {
      await ecoStore.getDocuments();
    }
    documents.value = ecoStore.documents as UserDocument[];
    console.log(props.category);
    if (props.category === 'consultations') {
      viewByVisits.value = true;
    } else {
      selectedCategory.value = props.category ?? 'directions';
    }
  } catch (error) {
    console.error('Ошибка при загрузке документов:', error);
  } finally {
    loading.value = false;
  }
});

const categoryOptions = [
  { label: 'Направления', value: 'directions' },
  { label: 'На подпись', value: 'goskluch' },
  { label: 'Справки и заключения', value: 'conclusions' },
  { label: 'Протоколы исследований', value: 'researches' },
  { label: 'Остальные документы', value: 'others' },
];

const periodFrom = ref('');

const rememberRangeStart = (date: { day: number, month: number, year: number }) => {
  const dd = String(date.day).padStart(2, '0');
  const mm = String(date.month).padStart(2, '0');
  const yyyy = String(date.year);

  periodFrom.value = `${dd}.${mm}.${yyyy}`;
};

const setPeriod = () => {
  if (!periodFrom.value) {
    $q.notify({
      message: 'Укажите начало периода',
      color: 'warning',
      textColor: 'dark',
      icon: 'today',
    });
    return;
  }
  dateRange.value = {
    from: periodFrom.value,
    to: formatDate(TODAY),
  };
  closePopup();
};

const handleRangeUpdate = (val: { from?: string; to?: string } | string) => {
  if (!val) return;

  if (typeof val === 'string') {
    dateRange.value = {
      from: val,
      to: val,
    };
    return;
  }

  if (val.from && !val.to) {
    dateRange.value = {
      from: val.from,
      to: val.from,
    };
  } else {
    dateRange.value = val as { from: string; to: string };
  }
};

const formatDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const parseDate = (str: string): Date | null => {
  const [d, m, y] = str.split('.').map(Number);
  if (!d || !m || !y) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Дата ВИЗИТА документа (datebegin), а не дата самого документа.
// Именно по ней сортируем — сортировка отталкивается от визита.
const getVisitDayTimestamp = (doc: { datebegin?: string }): number | null => {
  if (!doc.datebegin) return null;
  const date = parseDate(doc.datebegin);
  return date ? date.getTime() : null;
};

// Время документа в минутах от начала суток, секунды отбрасываем
const getMinutesOfDay = (doc: { time_doc?: string }): number => {
  if (!doc.time_doc) return 0;
  const [hh, mm] = doc.time_doc.split(':').map(Number);
  return (hh || 0) * 60 + (mm || 0);
};

// Сравнение документов: сначала по дате визита (направление dateSortAsc),
// при совпадении визита — по времени документа внутри дня (направление timeSortAsc).
// Документы без datebegin (визит-отстойник "без даты") всегда уходят в конец списка.
const compareDocs = (a: UserDocument, b: UserDocument): number => {
  const dayA = getVisitDayTimestamp(a);
  const dayB = getVisitDayTimestamp(b);

  if (dayA === null && dayB === null) {
    const timeA = getMinutesOfDay(a);
    const timeB = getMinutesOfDay(b);
    return timeSortAsc.value ? timeA - timeB : timeB - timeA;
  }
  if (dayA === null) return 1;
  if (dayB === null) return -1;

  if (dayA !== dayB) {
    return dateSortAsc.value ? dayA - dayB : dayB - dayA;
  }

  const timeA = getMinutesOfDay(a);
  const timeB = getMinutesOfDay(b);

  return timeSortAsc.value ? timeA - timeB : timeB - timeA;
};
const dateRange = ref<{ from: string; to: string } | null>({
  from: '01.01.2024',
  to: formatDate(TODAY),
});

const periodText = ref(
  `${dateRange.value?.from} — ${dateRange.value?.to}`,
);

const errorMessage = ref('');

const dateRangeOptions = (date: string) => {
  const [y, m, d] = date.split('/').map(Number);
  const dt = new Date(y, m - 1, d);

  return dt >= MIN_DATE && dt <= TODAY;
};

watch(dateRange, (val: { to: string, from: string } | null) => {
  if (!val?.from || !val?.to) return;

  const from = parseDate(val.from);
  const to = parseDate(val.to);

  if (!from || !to) return;

  if (from < MIN_DATE || to > TODAY) {
    errorMessage.value = 'Период должен быть с 01.01.2019 по сегодняшний день';
    return;
  }

  errorMessage.value = '';
  periodText.value = `${val.from} — ${val.to}`;
});

const closePopup = () => {
  datePopup.value?.hide();
};

type Category = 'researches' | 'conclusions' | 'directions' | 'goskluch' | 'others';

// Только время документа (часы:минуты), секунды не показываем.
// Возвращает пустую строку, если времени нет.
const formatTimeOnly = (doc: { time_doc?: string }): string => {
  if (!doc.time_doc) return '';

  const [hh, mm] = doc.time_doc.split(':');
  if (!hh || !mm) return '';

  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
};

const getDocumentCategory = (
  doc: { doc_type: number; is_napravlenie: number; goskluch: boolean },
): Category => {
  if (doc.goskluch === true) return 'goskluch';
  if (doc.is_napravlenie === 1) return 'directions';
  if (CONCLUSION_DOC_TYPES.has(doc.doc_type)) return 'conclusions';
  if (RESEARCH_DOC_TYPES.has(doc.doc_type)) return 'researches';
  return 'others';
};

const categoryFiltered = computed(() => {
  if (!documents.value.length) return [];

  return documents.value.filter((doc) => {
    if (!selectedCategory.value) return true;

    return getDocumentCategory(doc) === selectedCategory.value;
  });
});

const filteredByCategoryAndPeriod = computed(() => {
  if (!documents.value.length) return [];

  let from = new Date(2024, 0, 1);
  let to = new Date(2099, 11, 31);

  if (dateRange.value?.from && dateRange.value?.to) {
    const parsedFrom = parseDate(dateRange.value.from);
    const parsedTo = parseDate(dateRange.value.to);

    if (parsedFrom && parsedTo) {
      from = parsedFrom;
      to = parsedTo;
    }
  }

  return categoryFiltered.value
    .filter((doc) => {
      const d = parseDate(doc.date_doc);
      if (!d) return false;
      return d >= from && d <= to;
    })
    .sort(compareDocs);
});

const parseInput = () => {
  const parts = periodText.value.split('—').map((s) => s.trim());
  if (parts.length !== 2) return;

  const from = parseDate(parts[0]);
  const to = parseDate(parts[1]);

  if (!from || !to) {
    errorMessage.value = 'Некорректный формат даты';
    return;
  }

  if (from < MIN_DATE || to > TODAY) {
    errorMessage.value = 'Доступный для выбора период с 01.01.2019 по сегодняшний день';
    return;
  }

  errorMessage.value = '';
  dateRange.value = {
    from: formatDate(from),
    to: formatDate(to),
  };
};

const NOT_STARTED_KEY = '__not_started__';

const groupedByVisit = computed(() => {
  const map = new Map<string, UserDocument[]>();

  documents.value.forEach((doc) => {
    const key = doc.datebegin ?? NOT_STARTED_KEY;

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(doc);
  });

  const groups = Array.from(map.entries())
    .map(([date, docs]) => ({
      date,
      label: date === NOT_STARTED_KEY ? 'Без даты' : `Визит ${date}`,
      docs: [...docs].sort(compareDocs),
    }))
    .sort((a, b) => {
      if (a.date === NOT_STARTED_KEY) return -1;
      if (b.date === NOT_STARTED_KEY) return 1;

      const da = parseDate(a.date);
      const db = parseDate(b.date);

      if (da && db) {
        return dateSortAsc.value
          ? da.getTime() - db.getTime()
          : db.getTime() - da.getTime();
      }

      if (db) return 1;
      if (da) return -1;

      return 0;
    });

  return groups;
});

const openInNewTab = async (doc: UserDocument) => {
  const path = await ecoStore.getFilePath(doc.id_document);
  if (path[0].hash) {
    window.open(`https://host2.medsafe.tech/${path[0].hash}`, '_tab');
  } else {
    useMainStore().showMessage('Извините, не смогли найти документ.');
  }
};

// ---- Подписание ГОСКЛЮЧОМ ----
// signed: -1 — ошибка, 0 — ожидаем подписания, 1 — подписано
const signStatusRefreshing = ref(false);
const signingDocId = ref<number | null>(null);

const signStatusMap = computed(() => new Map<number, GoskluchSignStatus>(
  ecoStore.signStatuses.map((s) => [Number(s.id_doc), s]),
));

const getSignStatus = (doc: UserDocument) => signStatusMap.value.get(doc.id_document) ?? null;

const getSignedValue = (doc: UserDocument) => {
  const status = getSignStatus(doc);
  return status ? Number(status.signed) : null;
};

const isSignPending = (doc: UserDocument) => getSignedValue(doc) === 0;
const isSignError = (doc: UserDocument) => getSignedValue(doc) === -1;

const getSignStatusClass = (doc: UserDocument) => {
  const signed = getSignedValue(doc);
  if (signed === 1) return 'doc-sign-status--signed';
  if (signed === -1) return 'doc-sign-status--error';
  if (signed === 0) return 'doc-sign-status--pending';
  return '';
};

const isDocBusy = (doc: UserDocument) => signStatusRefreshing.value
  || signingDocId.value === doc.id_document;

// Подписать можно, пока не отправляли или если подписание завершилось ошибкой
const canSign = (doc: UserDocument) => doc.goskluch === true
  && (!getSignStatus(doc) || isSignError(doc));

const getSignMenuLabel = (doc: UserDocument) => (isSignError(doc)
  ? 'Подписать ГОСКЛЮЧОМ повторно'
  : 'Подписать ГОСКЛЮЧОМ');

// Кнопка обновления — когда ждём подписания; во время отправки служит лоадером
const showRefreshBtn = (doc: UserDocument) => isSignPending(doc)
  || signingDocId.value === doc.id_document;

const refreshSignStatuses = async () => {
  signStatusRefreshing.value = true;
  try {
    await ecoStore.getSignStatuses();
  } catch (error) {
    console.error('Ошибка при получении статусов подписания:', error);
  } finally {
    signStatusRefreshing.value = false;
  }
};

const { askSignType, signDocument } = useGoskluchSign();

const handleSign = async (doc: UserDocument) => {
  if (signingDocId.value === doc.id_document) return;
  const signType = await askSignType();
  if (!signType) return;
  signingDocId.value = doc.id_document;
  try {
    const sent = await signDocument(doc.id_document, signType);
    if (sent) await refreshSignStatuses();
  } finally {
    signingDocId.value = null;
  }
};

const download = async (doc: UserDocument) => {
  const path = await ecoStore.getFilePath(doc.id_document);
  const fileUrl = `https://host2.medsafe.tech/${path[0].hash}`;

  const response = await fetch(fileUrl);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = doc.doc_name;
  link.click();

  URL.revokeObjectURL(blobUrl);
};
</script>

<style scoped>
.docs-wrapper {
  padding: 18px 20px 40px;

  max-width: 1150px;
  margin: 0 auto 50px;
  background: white;
  border-radius: 26px;

  box-shadow: 0 -8px 30px rgba(0,0,0,0.08);
}

.info-banner {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
}

.info-accent-bar {
  width: 4px;
  height: 100%;
  background: #1976d2;
  border-radius: 4px;
  position: absolute;
  left: 0;
  top: 0;
}

.filter-label {
  font-size: 14px;
  color: #6b7280;
}

.filter-select,
.filter-input {
  background: #fff;
}

.doc-card {
  position: relative;
  padding: 16px 48px 16px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.15s ease;
}

.doc-card:hover {
  background: #f0f4ff;
}

.doc-card:active {
  background: #e8eef8;
}

.doc-date-label {
  font-size: 13px;
  color: #9ca3af;
}

.doc-time-label {
  color: #9ca3af;
}

.doc-title {
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  line-height: 1.35;
}

.doc-org {
  font-size: 13px;
  color: #6b7280;
}

.doc-actions {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

.doc-card--wide-actions {
  padding-right: 88px;
}

.doc-sign-status {
  font-weight: 500;
}

.doc-sign-status--pending {
  color: #ea580c;
}

.doc-sign-status--signed {
  color: #16a34a;
}

.doc-sign-status--error {
  color: #dc2626;
}

.visit-header {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}
.segment-toggle {
  display: inline-flex;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
}

.sort-links-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.sort-link {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #1976d2;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.sort-link:hover {
  opacity: 0.8;
}

.segment-btn {
  display: flex;
  align-items: center;
  padding: 8px 18px;
  border: none;
  border-radius: 7px;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  background: transparent;
  color: #555;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  white-space: nowrap;
}

.segment-btn--active {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.segment-btn:hover:not(.segment-btn--active) {
  background: rgba(255, 255, 255, 0.5);
}
.q-breadcrumbs {
  background-color: #eff5f9;
  padding: 0.5rem;
  border-radius: 0.5rem;
}
</style>
